// The town burning down (sim/destruct.ts drives it; town_ruin.ts lays the mesh out for it).
// Reads each building's stage and hp from the island's World every frame and turns them into:
//   scorch   - char creeping over the walls as the hp falls (atlas shader, per building uniform)
//   smoulder - grey smoke drifting off the roof, embers glowing in the char
//   burning  - flame tongues out of the windows and roofline, a black smoke column
//   collapse - the walls crumple into a dust cloud (vertex shader) and the rubble pile is revealed
// One vec4 per building in a uniform array and a few index writes on collapse: no new draws, no
// rebuilds, nothing allocated while it burns. Particles are rate-limited and dropped with distance
// and quality so a burning street can't eat the frame.

import * as THREE from 'three';
import type { World } from '../../../sim/world';
import { BODY, GAP, HOLE_VY, holeSeed } from './town_breach';

/** town_breach.ts outline(pts, e) as GLSL: piecewise linear in e, 0 outside */
function outlineGlsl(pts: readonly (readonly [number, number])[]) {
  let g = '0.0';
  for (let i = pts.length - 1; i >= 1; i--) {
    const [x0, y0] = pts[i - 1], [x1, y1] = pts[i];
    g = `( e <= ${x1.toFixed(3)} ? mix( ${y0.toFixed(3)}, ${y1.toFixed(3)}, ( e - ${x0.toFixed(3)} ) / ${(x1 - x0).toFixed(3)} ) : ${g} )`;
  }
  return `( e < ${pts[0][0].toFixed(3)} ? 0.0 : ${g} )`;
}
import { BREACH, BStage } from '../../../sim/destruct';
import type { RuinLayout } from './town_ruin';

/** The bits of Fx this needs (client/render/fx/index.ts). */
export interface RuinFx {
  buildingSmoke(x: number, y: number, z: number, size: number, dark: number): void;
  buildingFlame(x: number, y: number, z: number, size: number): void;
  buildingGlow(x: number, y: number, z: number, r: number): void;
  buildingDust(x: number, y: number, z: number, size: number): void;
  buildingCollapse(x: number, y0: number, z: number, hx: number, hz: number, h: number): void;
}

export const MAX_RUIN = 24;
const COLLAPSE_SECS = 1.5;
/** the rubble keeps smouldering this long after it falls */
const EMBER_SECS = 18;

const VERT_DECL = /* glsl */ `
attribute float bld;
uniform vec4 uBld[ ${MAX_RUIN} ];
uniform float uRuinY;
varying vec3 vRuin;
varying vec3 vRuinWorld;
`;
// x = damage 0..1, y = fire glow 0..1, z = collapse 0..1, w = lean direction (radians)
const VERT = /* glsl */ `
  vRuin = vec3( 0.0 );
  {
    int bi = int( abs( bld ) + 0.5 ) - 1;
    if ( bi >= 0 && bi < ${MAX_RUIN} ) {
      vec4 s = uBld[ bi ];
      // rubble is always half charred; the building chars as it's hurt
      vRuin = bld < 0.0 ? vec3( 0.85, s.y, -1.0 ) : s.xyz;
      if ( bld > 0.0 && s.z > 0.0 ) {
        // the collapse: storeys fold down onto the street, the top falls furthest and leans
        float k = pow( s.z, 1.4 );
        float hgt = max( transformed.y - uRuinY, 0.0 );
        float wob = sin( transformed.x * 1.7 + transformed.z * 2.3 ) * 0.5 + 0.5;
        transformed.y = uRuinY + hgt * ( 1.0 - k * ( 0.86 + wob * 0.1 ) ) - k * 0.6;
        vec2 lean = vec2( cos( s.w ), sin( s.w ) ) * hgt * k * 0.45;
        transformed.x += lean.x; transformed.z += lean.y;
      }
    }
    // loose pieces (town_breach.ts torn boards): never cut by a hole, never charred
    if ( bld < -100.0 ) vRuin = vec3( 0.0, 0.0, -2.0 );
    vRuinWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;
  }
`;
const FRAG_DECL = /* glsl */ `
varying vec3 vRuin;
varying vec3 vRuinWorld;
uniform float uRuinTime;
uniform float uRuinY;
float ruinHash( vec3 p ) { p = fract( p * 0.3183099 + 0.1 ); p *= 17.0; return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) ); }
float ruinNoise( vec3 x ) {
  vec3 i = floor( x ), f = fract( x );
  f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( mix( ruinHash( i ), ruinHash( i + vec3( 1, 0, 0 ) ), f.x ), mix( ruinHash( i + vec3( 0, 1, 0 ) ), ruinHash( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
              mix( mix( ruinHash( i + vec3( 0, 0, 1 ) ), ruinHash( i + vec3( 1, 0, 1 ) ), f.x ), mix( ruinHash( i + vec3( 0, 1, 1 ) ), ruinHash( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}
float vRuinChar = 0.0;
uniform vec4 uHole[ ${BREACH.maxHoles} ];
uniform vec4 uHoleRow[ ${BREACH.maxHoles} ];
uniform vec2 uHoleFit[ ${BREACH.maxHoles} ];
// town_breach.ts holeHalf / holeMid: how far course k reaches either side of its middle (< 0 = above or below the hole)
float holeHalfW( float k, float s, float sd, float t, vec4 hr, float fit ) {
  float e = ( ( k + 0.5 ) * hr.x + hr.y ) / ${(BREACH.r).toFixed(3)} / ${HOLE_VY.toFixed(3)};
  if ( abs( e ) >= 1.0 ) return -1.0;
  float w = ( 0.88 + 0.2 * ruinHash( vec3( k * 1.7 + 0.3, s * 3.1 + 5.0, sd ) ) ) * ${outlineGlsl(BODY)}
    + ( ruinHash( vec3( k * 2.9 + 1.1, s * 1.3 + 2.0, sd ) ) - 0.5 ) * t * 0.35;
  if ( abs( e ) < 0.75 && ruinHash( vec3( k * 3.7 + 0.9, s * 2.3 + 1.0, sd ) ) < 0.12 ) w += 0.22 * ( 0.6 + 0.4 * ruinHash( vec3( k * 1.1 + 4.0, s * 0.7 + 3.0, sd ) ) );
  w += ( 0.2 + 0.3 * ruinHash( vec3( k * 2.2 + 3.1, s * 3.3 + 1.0, sd ) ) )
    * ( abs( fract( t * ( 0.6 + 0.5 * ruinHash( vec3( k * 0.9 + 5.7, s * 2.6 + 4.0, sd ) ) ) + ruinHash( vec3( k * 4.3 + 0.2, s * 1.9 + 6.0, sd ) ) ) - 0.5 ) - 0.25 );
  return ${(BREACH.r).toFixed(3)} * fit * w;
}
float holeGapW( float k, vec4 hr, float fit ) {
  float e = ( ( k + 0.5 ) * hr.x + hr.y ) / ${(BREACH.r).toFixed(3)} / ${HOLE_VY.toFixed(3)};
  float g = ${(BREACH.r).toFixed(3)} * fit * ${outlineGlsl(GAP)};
  return g < 0.1 ? 0.0 : g;
}
float holeMid( float k, float sd, vec4 hr, float fit ) {
  float lean = ( ruinHash( vec3( sd * 0.7 + 1.3, 7.0, 3.0 ) ) < 0.5 ? -1.0 : 1.0 ) * ( 0.06 + 0.06 * ruinHash( vec3( sd * 1.3 + 2.1, 5.0, 9.0 ) ) );
  return lean * fit * ( ( k + 0.5 ) * hr.x + hr.y );
}
`;
// holes flown through the walls (town_breach.ts draws the room behind): a ragged outline cut in the wall's plane.
// xyz = the hole's centre on the wall, w = face (0 +x, 1 -x, 2 +z, 3 -z) + 4 * outline seed (town_breach.ts holeSeed); -1 = no hole
const FRAG_HOLE = /* glsl */ `
  #ifndef HOLE_PAD
  #define HOLE_PAD 0.0
  #endif
  float holeRim = 0.0;
  if ( vRuin.z > -0.5 ) {
    for ( int i = 0; i < ${BREACH.maxHoles}; i++ ) {
      vec4 h = uHole[ i ];
      if ( h.w < -0.5 ) continue;
      vec3 d = vRuinWorld - h.xyz;
      float face = mod( h.w, 4.0 ), sd = floor( h.w / 4.0 );
      float nrm = face < 1.5 ? d.x : d.z, u = face < 1.5 ? d.z : d.x;
      // only the skin of the wall he hit (uHoleRow.z = its plane): on plain siding the posts and trim standing proud of it
      // stay; a shopfront (glass, panels, frames, posters at several depths) is smashed through all of it
      vec4 hr = uHoleRow[ i ];
      float dz = nrm * ( face < 0.5 || ( face > 1.5 && face < 2.5 ) ? 1.0 : -1.0 ) - hr.z;
      if ( abs( dz ) > hr.w + HOLE_PAD ) continue;
      // layers set back behind it (a shop window's glass and blinds), seen at an angle past the edge of the face in
      // front, read as more of the broken wall's thickness rather than dark glass
      float back = -dz > 0.08 ? 0.12 : 0.0;
      // broken along the siding: each course of boards snaps at its own length either side (town_breach.ts holeHalf),
      // so the outline steps board by board, inside an upright body-sized oval
      float k = floor( ( d.y - hr.y ) / hr.x ), fit = uHoleFit[ i ].x, lim = uHoleFit[ i ].y;
      // each board's end is torn on a slant, not sawn square: t = -0.5 at the foot of the course, 0.5 at its top
      float t = fract( ( d.y - hr.y ) / hr.x ) - 0.5;
      float c = holeMid( k, sd, hr, fit ), s = u < c ? -1.0 : 1.0;
      float w = min( holeHalfW( k, s, sd, t, hr, fit ), lim + ( abs( dz ) > 0.02 ? 0.1 : 0.0 ) - s * c ), g = holeGapW( k, hr, fit ), a = abs( u - c );
      if ( a < w + HOLE_PAD && a > g - HOLE_PAD ) discard;
      if ( a < w + back && a > g - back ) holeRim = 1.0;
      // the paint is knocked off round the break, showing the raw board, wider or narrower board by board and now and
      // then not at all: along the snapped ends of this course, and along the edge of the course above or below
      // where that one is open and this isn't
      float rh = ruinHash( vec3( k * 5.3 + 0.7, s * 2.7 + 1.0, sd ) );
      float rw = rh < 0.5 ? 0.0 : 0.004 + 0.012 * ruinHash( vec3( k * 3.1 + 2.3, s * 4.1 + 0.5, sd ) );
      if ( w > 0.0 && a < w + rw && a > g - rw ) holeRim = 1.0;
      float kn = t > 0.0 ? k + 1.0 : k - 1.0, cn = holeMid( kn, sd, hr, fit ), an = abs( u - cn );
      float sn = u < cn ? -1.0 : 1.0, wn = min( holeHalfW( kn, sn, sd, t > 0.0 ? -0.5 : 0.5, hr, fit ), lim - sn * cn ), gn = holeGapW( kn, hr, fit );
      if ( wn > 0.0 && an < wn && an > gn && ( 0.5 - abs( t ) ) * hr.x < rw ) holeRim = 1.0;
    }
  }
`;
// the raw wood round a break: pale, grainy, a touch darker in the splits
const FRAG_RIM = /* glsl */ `
  if ( holeRim > 0.5 ) {
    float g = ruinHash( floor( vRuinWorld * vec3( 9.0, 60.0, 9.0 ) ) );
    diffuseColor.rgb = vec3( 0.3, 0.19, 0.1 ) * ( 0.78 + 0.34 * g );
  }
`;
// after the atlas paint: char and soot blotches grow out of the noise as the damage climbs
const FRAG_COLOR = /* glsl */ `
  if ( vRuin.x > 0.001 ) {
    vec3 wp = vHazeWorld;
    float n = ruinNoise( wp * 0.55 ) * 0.62 + ruinNoise( wp * 2.3 ) * 0.38;
    // char climbs from the foot of the walls and out of the openings as the damage grows
    float edge = 1.06 - vRuin.x * 0.72;
    float ch = smoothstep( edge, edge + 0.07, n );
    float soot = smoothstep( edge - 0.16, edge, n ) * ( 1.0 - ch );
    diffuseColor.rgb *= 1.0 - soot * 0.5;
    diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.07, 0.055, 0.045 ), ch * 0.9 );
    vRuinChar = ch;
  }
`;
// glowing embers in the deepest char, flickering; full flame-light while it burns
const FRAG_EMIT = /* glsl */ `
  if ( vRuin.y > 0.001 ) {
    vec3 wp = vHazeWorld;
    float fl = 0.6 + 0.4 * sin( uRuinTime * 7.0 + wp.x * 1.3 + wp.y * 2.1 ) * sin( uRuinTime * 4.3 + wp.z * 1.7 );
    // thin glowing cracks threading through the char, never whole patches
    float rn = ruinNoise( wp * 2.6 + vec3( 0.0, -uRuinTime * 0.25, 0.0 ) );
    float cr = ( 1.0 - smoothstep( 0.0, 0.022, abs( rn - 0.5 ) ) ) * smoothstep( 0.55, 0.75, ruinNoise( wp * 0.7 + 7.3 ) );
    outgoingLight += vec3( 1.0, 0.34, 0.07 ) * vRuinChar * cr * fl * vRuin.y * 0.75;
    // the rooms are alight: the glass of every window glows from inside (not the frames)
    float wlum = dot( texel.rgb, vec3( 0.333 ) );
    float isWin = vGlow > 0.5 && ( vGlow < 1.5 || vGlow > 3.5 ) ? 1.0 : 0.0;
    float win = isWin * ( 1.0 - smoothstep( 0.1, 0.34, wlum ) ) * ( 1.0 - vRuinChar * 0.6 ) * ( 1.0 - smoothstep( 0.0, 0.3, vRuin.z ) );
    outgoingLight = mix( outgoingLight, vec3( 1.0, 0.42, 0.1 ) * ( 1.6 + fl * 1.2 ), win * smoothstep( 0.2, 0.9, vRuin.y ) );
    // the heap: embers glowing in the gaps low down, cooling with the glow
    if ( vRuin.z < -0.5 ) {
      float low = 1.0 - smoothstep( 0.0, 1.1, wp.y - uRuinY );
      float em = smoothstep( 0.58, 0.8, ruinNoise( wp * 1.9 + vec3( 0.0, uRuinTime * 0.15, 0.0 ) ) ) * low;
      outgoingLight += vec3( 1.0, 0.36, 0.08 ) * em * fl * vRuin.y * 2.4;
    }
    // the whole wall takes an orange cast from its own fire
    outgoingLight *= 1.0 + vec3( 0.35, 0.12, 0.0 ) * vRuin.y * vRuin.x;
  }
`;

interface Live {
  dmg: number; glow: number;
  stage: BStage;
  /** seconds since the collapse began (-1 = standing) */
  fallT: number;
  lean: number;
  smokeAcc: number; flameAcc: number; glowAcc: number;
}

export class RuinView {
  private u: { uBld: { value: THREE.Vector4[] }; uRuinY: { value: number }; uRuinTime: { value: number }; uHole: { value: THREE.Vector4[] }; uHoleRow: { value: THREE.Vector4[] }; uHoleFit: { value: THREE.Vector2[] } };
  private holeVer = -1;
  private live: Live[] = [];
  private idx: { solid?: THREE.BufferAttribute; cut?: THREE.BufferAttribute } = {};
  private first = true;
  fx: RuinFx | null = null;
  /** particle budget scale (quality) */
  rate = 1;
  /** camera position, for dropping far-off particles */
  readonly cam = new THREE.Vector3();

  constructor(private world: World, private layout: RuinLayout) {
    this.u = {
      uBld: { value: Array.from({ length: MAX_RUIN }, () => new THREE.Vector4()) },
      uRuinY: { value: world.town.y },
      uRuinTime: { value: 0 },
      uHole: { value: Array.from({ length: BREACH.maxHoles }, () => new THREE.Vector4(0, 0, 0, -1)) },
      // per hole: x = siding course height, y = where a lap line sits (relative to the hole), z = the hit wall's plane
      // (outward offset from the hole's point), w = how deep either side of it the cut reaches; written by town_breach.ts
      uHoleRow: { value: Array.from({ length: BREACH.maxHoles }, () => new THREE.Vector4(0.2, 0, 0, 0.55)) },
      // per hole: how much narrower than a full body it is cut, to fit its panel (town_breach.ts)
      uHoleFit: { value: Array.from({ length: BREACH.maxHoles }, () => new THREE.Vector2(1, 9)) },
    };
    for (let i = 0; i < world.dest.buildings.length; i++) {
      this.live.push({ dmg: 0, glow: 0, stage: BStage.Intact, fallT: -1, lean: ((i * 2.399) % 6.283), smokeAcc: Math.random(), flameAcc: Math.random(), glowAcc: Math.random() });
    }
  }

  /** Index + tag attributes on the town geometry (the ink hull shares the solid one's index). */
  bindGeometry(g: THREE.BufferGeometry, which: 'solid' | 'cut') {
    const idx = new THREE.BufferAttribute(which === 'solid' ? this.layout.solidIdx : this.layout.cutIdx, 1);
    idx.setUsage(THREE.DynamicDrawUsage);
    g.setIndex(idx);
    g.setAttribute('bld', new THREE.BufferAttribute(which === 'solid' ? this.layout.solidBld : this.layout.cutBld, 1));
    this.idx[which] = idx;
  }

  /** Chain the scorch / crumple shader onto a town material (ink = only the crumple). */
  patch<T extends THREE.Material>(m: T, ink = false): T {
    const prev = m.onBeforeCompile;
    const key = m.customProgramCacheKey.bind(m);
    m.onBeforeCompile = (sh, r) => {
      prev.call(m, sh, r);
      Object.assign(sh.uniforms, this.u);
      // the ink hull writes its own begin_vertex, so the crumple goes in just before projection
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\n' + VERT_DECL)
        .replace('#include <project_vertex>', VERT + '\n#include <project_vertex>');
      // the ink hull is cut by the holes too (else its dark inside fills them)
      if (ink) {
        sh.fragmentShader = sh.fragmentShader
          .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
          // its shell stands out past the faces it outlines, so it's cut a little wider (else slivers of it show in the break)
          .replace('#include <color_fragment>', '#include <color_fragment>\n#define HOLE_PAD 0.12\n' + FRAG_HOLE);
        return;
      }
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
        .replace('#include <color_fragment>', '#include <color_fragment>\n' + FRAG_HOLE + FRAG_RIM + FRAG_COLOR)
        .replace('#include <opaque_fragment>', FRAG_EMIT + '\n#include <opaque_fragment>');
    };
    m.customProgramCacheKey = () => key() + '_ruin';
    return m;
  }

  update(dt: number, time: number) {
    this.u.uRuinTime.value = time;
    const d = this.world.dest, fx = this.fx;
    if (d.holeVer !== this.holeVer || this.first) this.syncHoles();
    const ty = this.world.town.y;
    for (let i = 0; i < d.buildings.length && i < MAX_RUIN; i++) {
      const b = d.buildings[i], L = this.live[i], k = b.box;
      // scorch tracks the hp, eased so a rocket's notch creeps on rather than pops
      const target = Math.min(1, (1 - b.hpByte / 255) * 0.92);
      L.dmg += (target - L.dmg) * Math.min(1, dt * (this.first ? 1e3 : 2.5));
      const glowT = b.stage === BStage.Burning ? 1 : b.stage === BStage.Smoulder ? 0.25 : 0;
      if (b.stage !== L.stage) {
        if (b.stage === BStage.Collapsed) { this.collapse(i, this.first); this.syncHoles(); }
        L.stage = b.stage;
      }
      if (L.fallT >= 0) {
        L.fallT += dt;
        if (L.fallT >= COLLAPSE_SECS && L.fallT - dt < COLLAPSE_SECS) this.hideBody(i);
        L.glow = Math.max(0, 1 - L.fallT / EMBER_SECS) * 0.8;
      } else L.glow += (glowT - L.glow) * Math.min(1, dt * 1.5);
      const fall = L.fallT < 0 ? 0 : Math.min(1, L.fallT / COLLAPSE_SECS);
      this.u.uBld.value[i].set(L.dmg, L.glow, fall, L.lean);

      // ---- particles
      if (!fx) continue;
      const far = this.cam.distanceTo(_v.set(k.x, ty, k.z));
      if (far > 140) continue;
      const near = far < 60 ? 1 : 0.5;
      const top = b.top;
      if (L.fallT >= 0 && L.fallT < COLLAPSE_SECS) {
        // dust boils off the falling top all the way down
        L.smokeAcc += dt * 5 * this.rate * near;
        const h = ty + (top - ty) * (1 - fall * 0.85);
        while (L.smokeAcc >= 1) { L.smokeAcc -= 1; const a = Math.random() * 6.283; fx.buildingDust(k.x + Math.cos(a) * (k.hx + 0.6), ty + Math.random() * (h - ty) * 0.7, k.z + Math.sin(a) * (k.hz + 0.6), 1.8); }
        continue;
      }
      if (L.fallT >= 0) {
        // the heap smoulders: thin smoke until the embers die
        if (L.fallT < EMBER_SECS) {
          L.smokeAcc += dt * 1.6 * this.rate * near * (1 - L.fallT / EMBER_SECS);
          while (L.smokeAcc >= 1) { L.smokeAcc -= 1; fx.buildingSmoke(k.x + (Math.random() - 0.5) * k.hx, ty + 0.8, k.z + (Math.random() - 0.5) * k.hz, 1.4, 0.5); }
        }
        continue;
      }
      if (b.stage >= BStage.Smoulder) {
        const dark = b.stage === BStage.Burning ? 1 : 0.15;
        L.smokeAcc += dt * (b.stage === BStage.Burning ? 6 : 1.8) * this.rate * near;
        while (L.smokeAcc >= 1) {
          L.smokeAcc -= 1;
          fx.buildingSmoke(k.x + (Math.random() - 0.5) * k.hx * 1.4, top - 0.3, k.z + (Math.random() - 0.5) * k.hz * 1.4, b.stage === BStage.Burning ? 2.1 : 1.3, dark);
        }
      }
      if (b.stage === BStage.Burning) {
        // its light on the street, a fresh pool every ~0.6 s
        L.glowAcc += dt;
        if (L.glowAcc > 0.6) { L.glowAcc = 0; fx.buildingGlow(k.x, ty, k.z + Math.sign(this.world.town.z - k.z) * (k.hz + 2), Math.max(k.hx, 3) * 1.5); }
        L.flameAcc += dt * 55 * this.rate * near;
        // the street face first (that's the side anyone sees), then the roof, then the ends
        const front = Math.sign(this.world.town.z - k.z) || 1;
        while (L.flameAcc >= 1) {
          L.flameAcc -= 1;
          const r = Math.random();
          if (r < 0.45) {
            // over the roofline: big tongues standing off the eaves and the ridge
            fx.buildingFlame(k.x + (Math.random() - 0.5) * k.hx * 1.9, top - 0.2 + Math.random() * 0.6, k.z + front * (0.3 + Math.random() * 0.7) * k.hz, 2.2 + Math.random() * Math.random() * 3.5);
          } else if (r < 0.85) {
            // out of the front: low and licking up the clapboard
            fx.buildingFlame(k.x + (Math.random() - 0.5) * k.hx * 1.9, ty + 0.3 + Math.random() * (top - ty - 1.2), k.z + front * (k.hz + 0.25), 0.7 + Math.random() * Math.random() * 2.2);
          } else {
            const s = Math.random() < 0.5 ? -1 : 1;
            fx.buildingFlame(k.x + s * (k.hx + 0.25), ty + 0.3 + Math.random() * (top - ty - 1.2), k.z + (Math.random() - 0.5) * k.hz * 1.8, 1.4);
          }
        }
      }
    }
    this.first = false;
  }

  /** the per-hole course spec the cut steps along (town_breach.ts fills it, same index as dest.holes) */
  get holeRows() { return this.u.uHoleRow.value; }
  get holeFits() { return this.u.uHoleFit.value; }

  private syncHoles() {
    const d = this.world.dest, hv = this.u.uHole.value;
    this.holeVer = d.holeVer;
    for (let i = 0; i < hv.length; i++) {
      const h = d.holes[i];
      if (h && d.buildings[h.b]?.stage !== BStage.Collapsed) hv[i].set(h.x, h.y, h.z, h.face + 4 * holeSeed(h)); else hv[i].w = -1;
    }
  }

  /** The walls come down: rubble in now (under the dust), the body out when the crumple ends. */
  private collapse(i: number, instant: boolean) {
    const L = this.live[i], r = this.layout.rubble[i];
    this.restore(this.idx.solid!, r.start, this.layout.rubbleIdx[i]);
    L.fallT = instant ? COLLAPSE_SECS + EMBER_SECS : 0;
    if (instant) { this.hideBody(i); return; }
    const k = this.world.dest.buildings[i].box, ty = this.world.town.y;
    this.fx?.buildingCollapse(k.x, ty, k.z, k.hx, k.hz, this.world.dest.buildings[i].top - ty);
  }

  private hideBody(i: number) {
    const s = this.layout.solid[i], c = this.layout.cut[i];
    this.zero(this.idx.solid!, s.start, s.count);
    if (c.count) this.zero(this.idx.cut!, c.start, c.count);
  }

  private zero(a: THREE.BufferAttribute, start: number, count: number) {
    (a.array as Uint32Array).fill(0, start, start + count);
    a.addUpdateRange(start, count);
    a.needsUpdate = true;
  }

  private restore(a: THREE.BufferAttribute, start: number, src: Uint32Array) {
    (a.array as Uint32Array).set(src, start);
    a.addUpdateRange(start, src.length);
    a.needsUpdate = true;
  }
}

const _v = new THREE.Vector3();
